import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps } from 'react-router-dom'
import {
  IonButtons,
  IonItem,
  IonLabel,
  IonSpinner,
  IonTitle,
} from '@ionic/react'
import cx from 'classnames'

import { CheckCircle } from '@/components/atoms/assets/CheckCircle'
import { Pin } from '@/components/atoms/assets/Pin'
import { Trash } from '@/components/atoms/assets/Trash'
import { BackButton } from '@/components/atoms/BackButton'
import { Button } from '@/components/atoms/Button'
import { Chip } from '@/components/atoms/Chip'
import { Title } from '@/components/atoms/Title'
import { DeleteConversationRepliesAlert } from '@/components/molecules/Alert/DeleteConversationRepliesAlert'
import { PinConversationRepliesAlert } from '@/components/molecules/Alert/PinConversationRepliesAlert'
import { Main } from '@/components/templates/main'

import { useConversation } from '@/lib/hooks/useConversation'
import { getThumbnailImage } from '@/utils/url'

import { IReply } from '@/interfaces'

interface PageProps {
  conversationId: UUID
}

export const ConversationManagement: FC<
  RouteComponentProps<{ conversationId: UUID }> & PageProps
> = ({ match }) => {
  const {
    params: { conversationId },
  } = match

  const [isDeletionModeActive, setDeletionMode] = useState(false)
  const [isPinModeActive, setPinMode] = useState(false)
  const [selectedReplies, setSelectedReplies] = useState([])
  const [isPinRepliesPopoverVisible, setIsPinRepliesPopoverVisible] =
    useState(false)
  const [isDeleteRepliesPopoverVisible, setIsDeleteRepliesPopoverVisible] =
    useState(false)

  const { t } = useTranslation('common')
  const {
    onGetConversation,
    conversation,
    isDeleteRepliesLoading,
    onDeleteReplies,
    isPinRepliesLoading,
    onPinReplies,
  } = useConversation()

  useEffect(() => {
    onGetConversation(conversationId)
  }, [conversationId, onGetConversation])

  const selectReply = (id: UUID) => {
    setSelectedReplies((replyIds) => [...replyIds, id])
  }

  const filterReply = (id: UUID) => {
    setSelectedReplies((replyIds) =>
      replyIds.filter((replyId) => replyId !== id)
    )
  }

  const isReplySelected = (id: UUID) => selectedReplies.includes(id)

  const toggleReply = (id: UUID) => {
    isReplySelected(id) ? filterReply(id) : selectReply(id)
  }

  const handlePinReplies = async () => {
    setIsPinRepliesPopoverVisible(false)
    await onPinReplies({ conversationId, replyIds: selectedReplies })
    setSelectedReplies([])
    setPinMode(false)
    onGetConversation(conversationId)
  }

  const handleDeleteReplies = async () => {
    setIsDeleteRepliesPopoverVisible(false)
    await onDeleteReplies({
      conversationId,
      replyIds: selectedReplies,
    })
    setSelectedReplies([])
    setDeletionMode(false)
    onGetConversation(conversationId)
  }

  const header = (
    <div className="flex justify-between items-center">
      <IonButtons slot="secondary">
        <BackButton defaultHref="/feed" />
      </IonButtons>
      <IonTitle>
        <Title level={3}>{t('UGC.conversation.manageTitle')}</Title>
      </IonTitle>
      {isDeletionModeActive && (
        <Button
          size="small"
          className="min-w-[5.5rem] danger"
          isDisabled={!selectedReplies.length}
          onClick={() => setIsDeleteRepliesPopoverVisible(true)}
        >
          {isDeleteRepliesLoading ? (
            <IonSpinner color="light" />
          ) : (
            t('common.delete')
          )}
        </Button>
      )}
      {isPinModeActive && (
        <Button
          size="small"
          className="min-w-[5.5rem]"
          isDisabled={!selectedReplies.length}
          onClick={() => setIsPinRepliesPopoverVisible(true)}
        >
          {isPinRepliesLoading ? <IonSpinner color="light" /> : t('common.pin')}
        </Button>
      )}
    </div>
  )

  return (
    <Main header={header} headerClassName="touch-none">
      <IonItem className="flex items-start">
        <div slot="start" className="mr-3 max-w-[6.25rem]">
          {conversation?.imageUrl && (
            <img
              className="full-w"
              src={getThumbnailImage(conversation?.imageUrl)}
            />
          )}
        </div>
        <div>
          <Title level={2}>{conversation?.title}</Title>
          <p className="text-xs text-cool-gray-200">{conversation?.caption}</p>
        </div>
      </IonItem>
      <IonItem mode="md">
        <IonLabel slot="start" className="font-bold">
          {t('UGC.conversation.submissions', {
            count: conversation?.replies.length,
          })}
        </IonLabel>
        <IonLabel slot="end">
          <IonButtons>
            <Button
              isDisabled={conversation?.replies.length <= 1}
              onClick={() => {
                setDeletionMode(false)
                setSelectedReplies([])
                setPinMode((value) => !value)
              }}
            >
              <Pin color={isPinModeActive ? 'primary' : 'default'} />
            </Button>
            <Button
              onClick={() => {
                setPinMode(false)
                setSelectedReplies([])
                setDeletionMode((value) => !value)
              }}
            >
              <Trash color={isDeletionModeActive ? 'danger' : 'default'} />
            </Button>
          </IonButtons>
        </IonLabel>
      </IonItem>
      <div className="w-full grid gap-[0.0625rem] grid-cols-3 lg:grid-cols-4 mb-4">
        {conversation?.replies.map((reply: IReply) => {
          return (
            <div
              key={reply?.id}
              className={cx(
                (isDeletionModeActive || isPinModeActive) && 'cursor-pointer',
                'relative'
              )}
              onClick={(e) => {
                if (isDeletionModeActive || isPinModeActive) {
                  e.stopPropagation()
                  toggleReply(reply?.id)
                }
              }}
            >
              <img
                src={getThumbnailImage(reply?.imageUrl)}
                className="w-full"
              />
              <div className="absolute right-1 top-1">
                {isDeletionModeActive && (
                  <CheckCircle
                    color="danger"
                    checked={isReplySelected(reply?.id)}
                  />
                )}
                {isPinModeActive && (
                  <div className="pr-[0.125rem] -mt-[0.125rem]">
                    <Chip
                      className={cx(
                        selectedReplies.indexOf(reply?.id) + 1
                          ? 'success'
                          : 'default',
                        'm-0 p-[0.4375rem] min-w-[1.3125rem] h-[1.3125rem]'
                      )}
                    >
                      {isReplySelected(reply?.id) &&
                        selectedReplies.indexOf(reply?.id) + 1}
                    </Chip>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
      <PinConversationRepliesAlert
        isOpen={isPinRepliesPopoverVisible}
        repliesNumber={selectedReplies.length}
        onConfirm={handlePinReplies}
        onDismiss={() => setIsPinRepliesPopoverVisible(false)}
      />
      <DeleteConversationRepliesAlert
        isOpen={isDeleteRepliesPopoverVisible}
        repliesNumber={selectedReplies.length}
        onConfirm={handleDeleteReplies}
        onDismiss={() => setIsDeleteRepliesPopoverVisible(false)}
      />
    </Main>
  )
}
